import { createContext, useContext, useMemo } from "react";

import {
  AvailableIntegrationFragment,
  IntegrationAbility,
} from "@/apollo/types";
import { useMountEffect } from "@/hooks/useMountEffect";
import { hasAbility, useIntegration } from "@/integrations";

import { useCurrentAccount } from "./account";

const DataWarehouseContext = createContext<{
  integration: AvailableIntegrationFragment;
  connectionId: string;
  features: {
    isTransformEnabled: boolean;
    isExtractEnabled: boolean;
    isActivateEnabled: boolean;
  };
} | null>(null);

export function useDataWarehouseContext() {
  const context = useContext(DataWarehouseContext);
  if (!context) {
    throw new Error(
      "useDataWarehouseContext must be used within a DataWarehouseProvider",
    );
  }
  return context;
}

export function DataWarehouseProvider(props: {
  children: React.ReactNode;
  onIntegrationNotFound?: (
    account: ReturnType<typeof useCurrentAccount>,
  ) => void;
}) {
  const account = useCurrentAccount();

  const integration = useIntegration(
    account.dataWarehouse?.integrationId ?? "",
  );

  const connectionId = account.dataWarehouse?.connectionId ?? "";

  const features = useMemo(() => {
    if (!integration) {
      return {
        isTransformEnabled: false,
        isExtractEnabled: false,
        isActivateEnabled: false,
      };
    }
    return {
      isTransformEnabled: [
        "snowflake",
        "bigquery",
        "weld-bigquery",
        "postgresql",
      ].includes(integration.id),
      isExtractEnabled: hasAbility(
        integration,
        IntegrationAbility.EltDestination,
      ),
      isActivateEnabled: hasAbility(
        integration,
        IntegrationAbility.ActivateSource,
      ),
    };
  }, [integration]);

  useMountEffect(() => {
    if (!integration && props.onIntegrationNotFound) {
      props.onIntegrationNotFound(account);
    }
  });

  if (!integration) {
    // eslint-disable-next-line no-console
    console.warn(
      "DataWarehouseProvider: Integration not found",
      account.dataWarehouse?.integrationId,
    );
    return null;
  }

  return (
    <DataWarehouseContext.Provider
      value={{ integration, features, connectionId }}
    >
      {props.children}
    </DataWarehouseContext.Provider>
  );
}
